import { useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { petShowOptions } from '$api-client/@tanstack/react-query.gen'
import { Card, LinkButton, NotFound, Text, Tooltip } from '$components'
import { EditIcon } from '$components'
import { PetRoutePaths } from '$features/pets/routes/protected'
import { AnimatedOutlet } from '$components'
import { useTenant } from '$features/auth/hooks/use-tenant'
import { SUBSCRIPTION_EXPIRED_MESSAGE } from '$constants/subscription'
import { usePageTitle } from '$components'
import { PetsFilterParam } from '$features/pets/enums/filter-params'
import { Router } from '$utils/router'

export function MedicalInformationTab() {
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const { hasSubscriptionExpired } = useTenant()

  const { data } = useQuery({
    ...petShowOptions({
      path: {
        pet: Number(id),
      },
      query: {
        include_deleted: true,
        [PetsFilterParam.IncludeUnapproved]: searchParams.get(PetsFilterParam.IncludeUnapproved) === '1',
      },
    }),
  })

  const pet = data?.data

  usePageTitle(pet ? `${pet.name} - Medical Information` : 'Medical Information')

  if (!pet) { return <NotFound /> }

  return (
    <div className="flex flex-col gap-stack-section">
      {/* Medical Details - Modal Editing */}
      <Card
        rounding="xl"
        padding="none"
      >
        <Card.Header
          padding="sm"
          className="flex justify-between items-center"
        >
          <Text type="h4">
            Medical Details
          </Text>
          {pet.approved_at && (
            <Tooltip
              content={hasSubscriptionExpired ? SUBSCRIPTION_EXPIRED_MESSAGE : undefined}
              disabled={!hasSubscriptionExpired}
            >
              <LinkButton
                variant="secondary"
                size="sm"
                to={Router.buildRoute(
                  PetRoutePaths.PetsMedicalDetailsEdit,
                )}
                icon={EditIcon}
                disabled={hasSubscriptionExpired}
              >
                Edit
              </LinkButton>
            </Tooltip>
          )}
        </Card.Header>

        <Card.Content
          padding="sm"
          className="flex flex-col gap-stack-roomy"
        >
          <div>
            <Card.Label>Microchip Number</Card.Label>
            <Text type="s">
              { pet.microchip_number ?? 'No microchip number' }
            </Text>
          </div>

          <div>
            <Card.Label>Weight</Card.Label>
            <Text type="s">
              { pet.weight ? `${pet.weight}kg` : 'No weight recorded' }
            </Text>
          </div>

          <div>
            <Card.Label>Vaccination Status</Card.Label>
            <Text type="s">
              { pet.vaccinated ? 'Vaccinated' : 'Not vaccinated' }
            </Text>
          </div>
        </Card.Content>
      </Card>

      {/* Veterinary Information - Modal Editing */}
      <Card
        rounding="xl"
        padding="none"
      >
        <Card.Header
          padding="sm"
          className="flex justify-between items-center"
        >
          <Text type="h4">
            Veterinary Information
          </Text>
          {pet.approved_at && (
            <Tooltip
              content={hasSubscriptionExpired ? SUBSCRIPTION_EXPIRED_MESSAGE : undefined}
              disabled={!hasSubscriptionExpired}
            >
              <LinkButton
                variant="secondary"
                size="sm"
                to={Router.buildRoute(
                  PetRoutePaths.PetsVeterinaryInformationEdit,
                )}
                icon={EditIcon}
                disabled={hasSubscriptionExpired}
              >
                Edit
              </LinkButton>
            </Tooltip>
          )}
        </Card.Header>

        <Card.Content
          padding="sm"
          className="flex flex-col gap-stack-roomy"
        >
          <div>
            <Card.Label>Veterinarian</Card.Label>
            <Text type="s">
              { pet.vet_name ?? 'No veterinarian' }
            </Text>
          </div>

          <div>
            <Card.Label>Contact Number</Card.Label>
            <Text type="s">
              { pet.vet_phone ?? 'No contact number' }
            </Text>
          </div>
        </Card.Content>
      </Card>

      {/* Medical Notes - Modal Editing */}
      <Card
        rounding="xl"
        padding="none"
      >
        <Card.Header
          padding="sm"
          className="flex justify-between items-center"
        >
          <Text type="h4">
            Medical Notes
          </Text>
          {pet.approved_at && (
            <Tooltip
              content={hasSubscriptionExpired ? SUBSCRIPTION_EXPIRED_MESSAGE : undefined}
              disabled={!hasSubscriptionExpired}
            >
              <LinkButton
                variant="secondary"
                size="sm"
                to={Router.buildRoute(
                  PetRoutePaths.PetsMedicalNotesEdit,
                )}
                icon={EditIcon}
                disabled={hasSubscriptionExpired}
              >
                Edit
              </LinkButton>
            </Tooltip>
          )}
        </Card.Header>

        <Card.Content
          padding="sm"
          className="flex flex-col gap-stack-roomy"
        >
          <div>
            <Card.Label>Allergies</Card.Label>
            <Text type="s">
              { pet.allergies ?? 'No allergies' }
            </Text>
          </div>

          <div>
            <Card.Label>Additional Notes</Card.Label>
            <Text type="s">
              { pet.medical_notes ?? 'No medical notes'}
            </Text>
          </div>
        </Card.Content>
      </Card>

      <AnimatedOutlet />
    </div>
  )
}
